import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';
import { useMe } from './use-me';
import * as permApi from '@shared/api/admin-permissions';
import type { SectionPermissionItem, AdminSection } from '@shared/types/api';

const authed = () => !!getTokenStore().getAccessToken();

export const ADMIN_PERMISSIONS_QUERY_KEY = ['admin', 'permissions'] as const;
export const MY_PERMISSIONS_QUERY_KEY = ['admin', 'my-permissions'] as const;

export function useNonSuperAdmins() {
  const { data: me } = useMe();
  const session = useAuthStore((s) => s.session);
  const currentAdminRole = me?.adminRole ?? session?.adminRole;
  const isSuperAdmin = currentAdminRole === 'SUPER_ADMIN';

  return useQuery({
    queryKey: [...ADMIN_PERMISSIONS_QUERY_KEY, 'admins'],
    queryFn: permApi.fetchNonSuperAdmins,
    enabled: authed() && isSuperAdmin,
    staleTime: 5_000,
  });
}

export function useUserPermissions(userId: string | null) {
  const { data: me } = useMe();
  const session = useAuthStore((s) => s.session);
  const currentAdminRole = me?.adminRole ?? session?.adminRole;
  const isSuperAdmin = currentAdminRole === 'SUPER_ADMIN';

  return useQuery({
    queryKey: [...ADMIN_PERMISSIONS_QUERY_KEY, 'user', userId],
    queryFn: () => (userId ? permApi.fetchUserPermissions(userId) : Promise.resolve([])),
    enabled: Boolean(authed() && isSuperAdmin && userId),
    staleTime: 5_000,
  });
}

export function useUpdateUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      permissions,
    }: {
      userId: string;
      permissions: SectionPermissionItem[];
    }) => permApi.updateUserPermissions(userId, permissions),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ADMIN_PERMISSIONS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: [...ADMIN_PERMISSIONS_QUERY_KEY, 'user', vars.userId] });
      qc.invalidateQueries({ queryKey: MY_PERMISSIONS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: [...MY_PERMISSIONS_QUERY_KEY, vars.userId] });
      toast.success('Admin permissions saved successfully');
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to update permissions',
      );
    },
  });
}

export function useMyPermissions() {
  const { data: me } = useMe();
  const session = useAuthStore((s) => s.session);
  const userId = me?.id ?? session?.id;

  return useQuery({
    queryKey: [...MY_PERMISSIONS_QUERY_KEY, userId],
    queryFn: permApi.fetchMyPermissions,
    enabled: authed() && Boolean(userId),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useSectionPermission(section: AdminSection) {
  const { data: me } = useMe();
  const session = useAuthStore((s) => s.session);
  const currentAdminRole = me?.adminRole ?? session?.adminRole;
  const isSuperAdmin = currentAdminRole === 'SUPER_ADMIN';

  const { data: myPermissions = [], isLoading } = useMyPermissions();

  if (isSuperAdmin) {
    return {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      isSuperAdmin: true,
      isLoading: false,
    };
  }

  const perm = myPermissions.find((p) => p.section === section);
  return {
    canView: Boolean(perm?.canView),
    canCreate: Boolean(perm?.canCreate),
    canEdit: Boolean(perm?.canEdit),
    canDelete: Boolean(perm?.canDelete),
    isSuperAdmin: false,
    isLoading,
  };
}
